import ToggleField from '@/components/Indicators/ToggleField'
import { buildWatches } from './notifications.js'

// Fecha + hora completas (no solo "15:00"): un log de varios días con solo
// la hora es ambiguo — no se distingue un cruce de ayer del de hoy.
function formatDateTime(seconds) {
  if (!Number.isFinite(seconds)) return ''
  return new Date(seconds * 1000).toLocaleString([], {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  })
}

// Sección "Notificaciones" REAL del panel de EMA/SMA Quantum: un toggle que
// activa/desactiva la vigilancia de cruces (precio×media, media×media) sobre
// las medias activas del usuario en el símbolo/TF actual, la lista dinámica
// de vigilancias vigentes, y el log de eventos ya detectados (persistido en
// config.notifications, ver hooks/useEmaSmaNotifications.js).
export default function EmaSmaNotificationsSection({ config, onChange }) {
  const notif = config.notifications || { enabled: false, events: [] }
  const activeMas = (config.mas || []).filter(m => m.active)
  const periodById = Object.fromEntries(activeMas.map(m => [m.id, m.period]))
  const watches = buildWatches(config.mas)
  // Vigilancias ordenadas: primero precio×media por periodo, luego parejas
  // por periodo (el de la media menor de cada par) — sin esto el orden
  // dependía de en qué orden se activaron las medias, no de algo legible.
  const priceWatches = watches
    .filter(w => w.kind === 'price_ma')
    .sort((a, b) => (periodById[a.maId] ?? 0) - (periodById[b.maId] ?? 0))
  const pairWatches = watches
    .filter(w => w.kind === 'ma_ma')
    .sort((a, b) => (periodById[a.smallerId] ?? 0) - (periodById[b.smallerId] ?? 0))

  const setEnabled = (enabled) => onChange({ ...config, notifications: { ...notif, enabled } })

  return (
    <div className="space-y-3">
      <ToggleField
        label="Notificaciones activas"
        value={notif.enabled}
        onChange={setEnabled}
        data-testid="ema-sma-notifications-toggle"
      />

      <div>
        <div className="text-[10px] font-semibold uppercase tracking-wider mb-1 text-[#B08D57] dark:text-[#D3B578]">
          Vigilancias
        </div>
        <div className="text-[9px] mb-1 text-[#4A5A52] dark:text-slate-400">
          Se generan de tus medias activas.
        </div>
        {priceWatches.length === 0 ? (
          <div className="text-[10px] text-center py-1 text-[#4A5A52] dark:text-slate-400">
            Activa al menos una media para vigilar cruces de precio.
          </div>
        ) : (
          <ul className="space-y-0.5" data-testid="ema-sma-watches-price">
            {priceWatches.map(w => (
              <li key={w.key} className="text-[10px] text-[#15211B] dark:text-slate-100">
                Precio × {w.maLabel}
              </li>
            ))}
          </ul>
        )}
        {activeMas.length < 2 ? (
          <div className="text-[10px] text-center py-1 mt-1 text-[#4A5A52] dark:text-slate-400">
            Activa al menos 2 medias para vigilar cruces entre medias.
          </div>
        ) : (
          <ul className="space-y-0.5 mt-1" data-testid="ema-sma-watches-pairs">
            {pairWatches.map(w => (
              <li key={w.key} className="text-[10px] text-[#15211B] dark:text-slate-100">
                {w.smallerLabel} × {w.largerLabel}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div>
        <div className="text-[10px] font-semibold uppercase tracking-wider mb-1 text-[#B08D57] dark:text-[#D3B578]">
          Eventos recientes
        </div>
        {notif.events.length === 0 ? (
          <div className="text-[10px] text-center py-1 text-[#4A5A52] dark:text-slate-400">
            Sin cruces detectados todavía.
          </div>
        ) : (
          <ul className="space-y-1 max-h-40 overflow-y-auto pr-1" data-testid="ema-sma-notification-events">
            {notif.events.map(ev => (
              <li key={ev.id} className="text-[10px] flex items-center justify-between gap-2">
                <span className="flex items-center gap-1 min-w-0 text-[#15211B] dark:text-slate-100">
                  <span className={`shrink-0 ${ev.direction === 'up' ? 'text-[#0B3D2E] dark:text-emerald-400' : 'text-[#B71C1C] dark:text-red-400'}`}>
                    {ev.direction === 'up' ? '↑' : '↓'}
                  </span>
                  <span className="truncate">{ev.message}</span>
                </span>
                <span className="shrink-0 text-[#4A5A52] dark:text-slate-400">{formatDateTime(ev.time)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
